import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { cn } from '@restworld/utils-common';
// The mobile metadata, like form-validators: one libphonenumber metadata set per bundle (RW-235, item 4).
import {
  getCountryCallingCode,
  isValidPhoneNumber as validatePhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input/mobile';

import CountriesMenu, { CountriesMenuProps, Country } from '../countries-menu';
import { TextInput, InputProps } from '../text-input';

export interface PhoneInputProps {
  id?: string;
  textInput?: Omit<InputProps, 'inputStyle' | 'iconStyle' | 'value' | 'onChange' | 'id'> & { iconStyle?: string };
  countriesMenu?: Omit<CountriesMenuProps, 'wrapperStyle' | 'variant' | 'value' | 'onChange'> & {
    wrapperStyle?: string;
  };
  defaultCountry?: Country;
  onChange?: (value: string) => void;
  value?: string;
  name?: string;
  ref?: React.Ref<unknown>;
  onBlur?: () => void;
  /** @deprecated Legacy props - use textInput/countriesMenu instead */
  priorityCountries?: string[];
  inputContainerStyles?: string[];
  variant?: string;
  // ReactNode, not just string: a field whose value already belongs to an account renders a
  // support text with a "log in instead" link (see AlreadyUsedLoginHelperText in worker-app).
  supportText?: string | ReactNode;
  placeholder?: string;
  /** Label rendered above the field, the way EmailInput does it. */
  label?: React.ReactNode;
  /** @deprecated Legacy prop - ignored */
  menuDialogStyles?: string[];
  /** @deprecated Legacy prop - ignored */
  testIds?: Record<string, string>;
  /** Disables both the phone field and the country selector. */
  disabled?: boolean;
}

// parsePhoneNumber throws on some malformed input rather than returning undefined, and a stored value we
// cannot read must never take a form down.
// Only ever parse a value that already carries its `+`. Prepending one to a bare national number makes
// libphonenumber read the first digits as a calling code: `3331234567`, an ordinary Italian mobile, comes
// back as FRANCE (+33). Values without a `+` therefore keep the old path untouched, so this change cannot
// break anything that worked before. Whether that path is right at all is a separate question, see the
// review note on the two call sites that disagree about adding the `+`.
const parseOrUndefined = (raw: string) => {
  if (!raw.startsWith('+')) return undefined;
  try {
    return parsePhoneNumber(raw);
  } catch {
    return undefined;
  }
};

export function PhoneInput({
  textInput,
  countriesMenu,
  onChange,
  id = 'phone',
  defaultCountry = 'IT',
  value,
  // Legacy props
  priorityCountries,
  inputContainerStyles: _inputContainerStyles,
  variant: legacyVariant,
  supportText: legacySupportText,
  placeholder: legacyPlaceholder,
  name: _name,
  // onBlur is a live prop (react-hook-form passes field.onBlur here to mark the field "touched").
  // The legacy @restworld/ui-ds PhoneInput wired it to the input; this rewrite dropped it, which broke
  // touched-tracking for phone fields (e.g. the add-collaborator form's Next gate). Wire it back below.
  onBlur,
  label,
  menuDialogStyles: _menuDialogStyles,
  testIds: _testIds,
  disabled,
}: PhoneInputProps) {
  // Merge legacy props into textInput/countriesMenu
  const mergedCountriesMenu =
    countriesMenu || (priorityCountries ? { favorites: priorityCountries as Country[] } : undefined);
  const mergedTextInput =
    textInput ||
    (legacyVariant || legacySupportText || legacyPlaceholder
      ? {
          isError: legacyVariant === 'error',
          supportText: legacySupportText,
          supportTextVariant: (legacyVariant === 'error' ? 'error' : 'default') as 'error' | 'default',
          placeholder: legacyPlaceholder,
          isFullwidth: true,
        }
      : undefined);

  const { wrapperStyle: _countryMenuWrapperStyle, favorites } = mergedCountriesMenu || {};
  const { wrapperStyle: _inputWrapperStyles, iconStyle: _iconStyle, ...restInputProps } = mergedTextInput || {};

  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(defaultCountry);
  // Gates the notify effect below until the mount effect has read `value`. Without it the very first render
  // hands the caller the default calling code, and anything watching the form (a validator, an autosave, a
  // dependent field) can act on that before the real value lands one render later.
  const [initialised, setInitialised] = useState(false);

  // Helper to remove spaces
  const removeSpaces = useCallback((str: string) => str.replace(/\s+/g, ''), []);

  // Helper to format phone number
  const formatPhoneNumber = useCallback((num: string) => {
    if (num.length > 3 && num[3] !== ' ') {
      return num.slice(0, 3) + ' ' + num.slice(3);
    }
    return num;
  }, []);

  // Set initial phone number from value.
  //
  // Parse the country out of the value instead of chopping three characters off the front. That chop
  // assumed every calling code is two digits, so it ate a digit of the number for +1 and left one behind
  // for +212, +216, +880, +977 and the rest. Worse, the selector then stayed on the default country and
  // the effect below rewrote the value with ITS calling code: opening the worker profile turned
  // +9779869639381 into +3979869639381, and saving anything on that screen would have persisted it.
  // Measured on 16/09/2026: 8.025 workers hold a number whose calling code is not two digits.
  useEffect(() => {
    if (value) {
      const parsed = parseOrUndefined(value);
      if (parsed?.country) {
        setSelectedCountry(parsed.country as Country);
        setPhoneNumber(parsed.nationalNumber.toString());
      } else if (value.startsWith('+')) {
        // The country cannot be told apart: a shared calling code with too few digits to disambiguate
        // (+1 is the US, Canada and the Caribbean; +7 is Russia and Kazakhstan), or a partial number.
        // Keep the value whole rather than guess. The notify effect passes anything starting with '+'
        // straight through, so the caller gets back exactly what it gave us. Recomposing it with the
        // default country's calling code, which is what this used to do, is how +1212 became +3912.
        setPhoneNumber(value);
      } else {
        setPhoneNumber(`+${getCountryCallingCode(selectedCountry)}${removeSpaces(value)}`);
      }
    }
    setInitialised(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent on change
  useEffect(() => {
    if (!initialised) return;
    if (onChange) {
      if (phoneNumber?.startsWith('+')) {
        onChange(phoneNumber);
      } else {
        onChange(`+${getCountryCallingCode(selectedCountry)}${removeSpaces(phoneNumber)}`);
      }
    }
  }, [initialised, onChange, phoneNumber, selectedCountry, removeSpaces]);

  // Handle country change
  const handleCountryChange = useCallback((country: Country) => setSelectedCountry(country), []);

  // Handle input change
  const handleInputChange = useCallback(
    (phone: string) => setPhoneNumber((prev) => (isNaN(+removeSpaces(phone)) ? prev : phone)),
    [removeSpaces]
  );

  const isError = restInputProps?.isError;
  const supportText = restInputProps?.supportText;
  const supportTextVariant = restInputProps?.supportTextVariant;
  const placeholder = restInputProps?.placeholder;

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={id} className="mb-2 block text-base font-medium text-neutral-700">
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'flex items-center border rounded-lg bg-neutral-10 h-12 hover:bg-neutral-30',
          'focus-within:border-primary-300 focus-within:ring-primary-300 focus-within:ring-1',
          isError ? 'border-red-500 !bg-red-50' : 'border-neutral-90',
          disabled && 'bg-neutral-40 hover:bg-neutral-40 cursor-not-allowed'
        )}
      >
        {/* Country selector */}
        <div className="flex items-center pl-2 pr-1 flex-shrink-0">
          <CountriesMenu
            value={selectedCountry}
            onChange={handleCountryChange}
            variant="phone"
            favorites={favorites}
            disabled={disabled}
            dropdownPlacementConfig={{ crossAxis: -8, mainAxis: 8 }}
          />
        </div>
        {/* Phone number input */}
        <input
          id={id}
          type="tel"
          className={cn(
            'flex-1 min-w-0 border-0 bg-transparent h-full pl-1 pr-3 text-base',
            'placeholder:!text-neutral-200 placeholder:truncate placeholder:text-base placeholder:font-normal',
            'focus:ring-0 focus:outline-none',
            isError && 'text-red-700 placeholder:!text-red-700',
            disabled && 'cursor-not-allowed text-neutral-200'
          )}
          value={formatPhoneNumber(phoneNumber)}
          onChange={(e) => handleInputChange(e.target.value)}
          disabled={disabled}
          onBlur={onBlur}
          placeholder={placeholder}
        />
      </div>
      {supportText && (
        <p
          className={cn(
            '!text-sm font-normal mt-2',
            supportTextVariant === 'error' || isError ? 'text-red-500' : 'text-neutral-700'
          )}
        >
          {supportText}
        </p>
      )}
    </div>
  );
}

export default PhoneInput;

/** True for a valid mobile number, like `isValidPhoneNumber` in form-validators (mobile metadata). */
export function checkIsPhoneNumberValid(phoneNumber?: string): boolean {
  if (!phoneNumber) return false;
  return validatePhoneNumber(phoneNumber);
}
